import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  IconButton,

  Grid,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  GraphicEq,
  Bolt,
  Schedule,
  ErrorOutline,
  RadioButtonChecked,
  TrendingUp,
} from '@mui/icons-material';
import Sidebar from '../landingPage/Sidebar';
import { PrimaryButton } from '../../components/ui/button';
import { PrimaryTextField } from '../../components/ui/input';
import { DataTable } from '../../components/ui/table';
import { PrimaryBreadcrumb } from '../../components/ui/breadcrumb';
import { useLocation } from 'react-router-dom';

const LiveStreamingPage = () => {
  const location = useLocation();
  const viewModeFromState = location.state?.viewMode || 'streaming';
  const [projectName, setProjectName] = useState('Project HTL');
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [isRunning, setIsRunning] = useState(true);
  const [activeView, setActiveView] = useState(viewModeFromState); // 'streaming' or 'preview'
  const [logs, setLogs] = useState([]);
  const logsEndRef = useRef(null);
  const logsContainerRef = useRef(null);

  // Dummy KPI data
  const kpiData = [
    {
      label: 'Throughput',
      value: '12.4k/S',
      icon: Bolt,
      bgColor: '#E0F2FE',
      iconColor: '#0284C7',
    },
    {
      label: 'Avg Latency',
      value: '23ms',
      icon: Schedule,
      bgColor: '#DCFCE7',
      iconColor: '#16A34A',
    },
    {
      label: 'Error Rate',
      value: '0.03%',
      icon: ErrorOutline,
      bgColor: '#FEE2E2',
      iconColor: '#DC2626',
    },
    {
      label: 'Active Partitions',
      value: '3',
      icon: GraphicEq,
      bgColor: '#F3E8FF',
      iconColor: '#9333EA',
    },
    {
      label: 'Consumer Lag',
      value: '45ms',
      icon: Schedule,
      bgColor: '#FEF3C7',
      iconColor: '#F59E0B',
    },
  ];

  // Dummy table data
  const tableData = [
    {
      srNo: 1,
      timestamp: '14:48:10',
      eventId: 'oo3a1an3u',
      status: 'Success',
      processingTime: '85ms',
    },
    {
      srNo: 2,
      timestamp: '14:48:09',
      eventId: 'oo3a1an3u',
      status: 'delayed',
      processingTime: '65ms',
    },
    {
      srNo: 3,
      timestamp: '14:48:08',
      eventId: 'oo3a1an3u',
      status: 'Success',
      processingTime: '72ms',
    },
    {
      srNo: 4,
      timestamp: '14:48:07',
      eventId: 'oo3a1an3u',
      status: 'Success',
      processingTime: '58ms',
    },
    {
      srNo: 5,
      timestamp: '14:48:06',
      eventId: 'oo3a1an3u',
      status: 'delayed',
      processingTime: '91ms',
    },
    {
      srNo: 6,
      timestamp: '14:48:05',
      eventId: 'oo3a1an3u',
      status: 'Success',
      processingTime: '67ms',
    },
    {
      srNo: 7,
      timestamp: '14:48:04',
      eventId: 'oo3a1an3u',
      status: 'Success',
      processingTime: '74ms',
    },
    {
      srNo: 8,
      timestamp: '14:48:03',
      eventId: 'oo3a1an3u',
      status: 'delayed',
      processingTime: '82ms',
    },
  ];

  // Generate dummy log messages
  const generateLogMessage = () => {
    const messages = [
      '[INFO] Event processed successfully: oo3a1an3u',
      '[DEBUG] Processing transaction batch: 142 records',
      '[INFO] Cache updated: 1,234 entries',
      '[WARN] High latency detected: 125ms',
      '[INFO] Consumer lag reduced: 45ms → 32ms',
      '[DEBUG] Partition rebalanced: 3 active partitions',
      '[INFO] Throughput maintained: 12.4k events/sec',
      '[INFO] Event processed successfully: oo3a1an3u',
      '[DEBUG] Database connection pool: 45/50 active',
      '[INFO] Cache hit rate: 94.2%',
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Auto-scroll to bottom when new logs arrive
  const scrollToBottom = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Simulate live log streaming
  useEffect(() => {
    if (activeView === 'streaming' && isRunning) {
      // Initialize with some logs
      const initialLogs = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        timestamp: new Date(Date.now() - (20 - i) * 1000).toLocaleTimeString(),
        message: generateLogMessage(),
      }));
      setLogs(initialLogs);

      // Add new log every 1-2 seconds
      const interval = setInterval(() => {
        setLogs((prevLogs) => {
          const newLog = {
            id: prevLogs.length,
            timestamp: new Date().toLocaleTimeString(),
            message: generateLogMessage(),
          };
          // Keep only last 100 logs
          const updatedLogs = [...prevLogs, newLog].slice(-100);
          return updatedLogs;
        });
      }, Math.random() * 1000 + 1000);

      return () => clearInterval(interval);
    }
  }, [activeView, isRunning]);

  const handleSave = () => {
    console.log('Save clicked');
  };

  const handlePause = () => {
    setIsRunning(false);
    console.log('Pause clicked');
  };

  const handleStop = () => {
    setIsRunning(false);
    console.log('Stop clicked');
  };

  const getStatusColor = (status) => {
    return status === 'Success' ? '#16A34A' : '#F59E0B';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Top Header Bar */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1, sm: 1.5 },
            bgcolor: '#0F172A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => window.history.back()}
              sx={{
                p: 0.5,
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <ArrowBack sx={{ fontSize: 20 }} />
            </IconButton>
            {isEditingProjectName ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PrimaryTextField
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onBlur={() => setIsEditingProjectName(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsEditingProjectName(false);
                    }
                  }}
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    input: { color: 'white', fontSize: '0.875rem', py: 0.5 },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'white',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {projectName}...
                </Typography>
                <IconButton
                  onClick={() => setIsEditingProjectName(true)}
                  sx={{
                    p: 0.25,
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  <Edit sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <PrimaryButton
              variant="outlined"
              onClick={handleSave}
              sx={{
                fontSize: '0.8125rem',
                textTransform: 'none',
                px: 2,
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Save
            </PrimaryButton>
            <PrimaryButton
              variant="outlined"
              onClick={handlePause}
              sx={{
                fontSize: '0.8125rem',
                textTransform: 'none',
                px: 2,
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Pause
            </PrimaryButton>
            <PrimaryButton
              variant="outlined"
              onClick={handleStop}
              sx={{
                fontSize: '0.8125rem',
                textTransform: 'none',
                px: 2,
                color: '#FF4444',
                borderColor: '#FF4444',
                bgcolor: 'transparent',
                '&:hover': {
                  borderColor: '#FF6666',
                  bgcolor: 'rgba(255, 68, 68, 0.1)',
                },
              }}
            >
              Stop
            </PrimaryButton>
            {isRunning && (
              <Chip
                icon={<GraphicEq sx={{ fontSize: 16, color: '#008236' }} />}
                label="Running"
                sx={{
                  height: 28,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  fontFamily: 'Inter, sans-serif',
                  bgcolor: '#DCFCE7',
                  color: '#008236',
                  border: '1px solid #008236',
                  borderRadius: '4px',
                  '& .MuiChip-icon': {
                    marginLeft: '8px',
                    marginRight: '0',
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Breadcrumbs */}
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 1, bgcolor: '#FAFBFD', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
          <PrimaryBreadcrumb
            items={[
              { label: 'Workflow', path: '/workflow' },
              { label: 'NLP Builder', path: '/natural-language-search' },
              { label: activeView === 'streaming' ? 'Live Streaming' : 'Data Preview' },
            ]}
            sx={{ mb: 0 }}
            linkColor="#585858"
            currentColor="#008236"
          />
        </Box>

        {/* Main Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1.5, sm: 2 },
            bgcolor: '#FAFBFD',
            overflow: 'auto',
            backgroundImage: 'radial-gradient(circle, #E5E7EB 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0',
          }}
        >
          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {kpiData.map((kpi, index) => {
              const IconComponent = kpi.icon;
              return (
                <Grid item xs={12} sm={6} md={2.4} key={index}>
                  <Card
                    sx={{
                      borderRadius: '4px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                      bgcolor: kpi.bgColor,
                      position: 'relative',
                      overflow: 'visible',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderColor: kpi.iconColor,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                        <IconComponent sx={{ fontSize: 20, color: kpi.iconColor }} />
                        <TrendingUp sx={{ fontSize: 14, color: kpi.iconColor, opacity: 0.6 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {kpi.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: '#0F172A',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {kpi.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Real time Events Streams / Data Preview Section */}
          <Card
            sx={{
              borderRadius: '4px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              bgcolor: 'white',
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#0F172A',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {activeView === 'streaming' ? 'Real time Events Streams' : 'Data Preview'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {activeView === 'streaming' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: 'pulseScale 2s ease-in-out infinite',
                          '@keyframes pulseScale': {
                            '0%, 100%': {
                              transform: 'scale(1)',
                            },
                            '50%': {
                              transform: 'scale(1.15)',
                            },
                          },
                        }}
                      >
                        <RadioButtonChecked sx={{ fontSize: 12, color: '#16A34A' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#16A34A',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          animation: 'fadePulse 2s ease-in-out infinite',
                          '@keyframes fadePulse': {
                            '0%, 100%': {
                              opacity: 1,
                            },
                            '50%': {
                              opacity: 0.7,
                            },
                          },
                        }}
                      >
                        Live
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, border: '1px solid #E5E7EB', borderRadius: '6px', p: 0.5 }}>
                    <PrimaryButton
                      variant={activeView === 'streaming' ? 'contained' : 'outlined'}
                      onClick={() => setActiveView('streaming')}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        bgcolor: activeView === 'streaming' ? '#008236' : 'transparent',
                        color: activeView === 'streaming' ? 'white' : '#525252',
                        borderColor: activeView === 'streaming' ? '#008236' : '#E5E7EB',
                        '&:hover': {
                          bgcolor: activeView === 'streaming' ? '#006B2E' : '#F9FAFB',
                        },
                      }}
                    >
                      Live Streaming
                    </PrimaryButton>
                    <PrimaryButton
                      variant={activeView === 'preview' ? 'contained' : 'outlined'}
                      onClick={() => setActiveView('preview')}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        bgcolor: activeView === 'preview' ? '#008236' : 'transparent',
                        color: activeView === 'preview' ? 'white' : '#525252',
                        borderColor: activeView === 'preview' ? '#008236' : '#E5E7EB',
                        '&:hover': {
                          bgcolor: activeView === 'preview' ? '#006B2E' : '#F9FAFB',
                        },
                      }}
                    >
                      Data Preview
                    </PrimaryButton>
                  </Box>
                </Box>
              </Box>

              {activeView === 'streaming' ? (
                /* Live Logs View */
                <Box
                  ref={logsContainerRef}
                  sx={{
                    bgcolor: '#0F172A',
                    borderRadius: '6px',
                    p: 2,
                    height: '500px',
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: '#1E293B',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: '#475569',
                      borderRadius: '4px',
                      '&:hover': {
                        bgcolor: '#64748B',
                      },
                    },
                  }}
                >
                  {logs.map((log) => (
                    <Box
                      key={log.id}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        mb: 1,
                        animation: 'fadeIn 0.3s ease-in',
                        '@keyframes fadeIn': {
                          from: {
                            opacity: 0,
                            transform: 'translateY(-10px)',
                          },
                          to: {
                            opacity: 1,
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#94A3B8',
                          fontFamily: 'monospace',
                          minWidth: '80px',
                        }}
                      >
                        {log.timestamp}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#E2E8F0',
                          fontFamily: 'monospace',
                          flexGrow: 1,
                        }}
                      >
                        {log.message}
                      </Typography>
                    </Box>
                  ))}
                  <div ref={logsEndRef} />
                </Box>
              ) : (
                /* Data Preview Table */
                <DataTable
                  columns={[
                    {
                      key: 'srNo',
                      label: 'Sr.No',
                      sortable: true,
                      cellSx: {
                        fontSize: '0.8125rem',
                        color: '#525252',
                        fontFamily: 'Inter, sans-serif',
                      },
                    },
                    {
                      key: 'timestamp',
                      label: 'Timestamp',
                      sortable: true,
                      cellSx: {
                        fontSize: '0.8125rem',
                        color: '#525252',
                        fontFamily: 'Inter, sans-serif',
                      },
                    },
                    {
                      key: 'eventId',
                      label: 'Event ID',
                      sortable: true,
                      cellSx: {
                        fontSize: '0.8125rem',
                        color: '#525252',
                        fontFamily: 'Inter, sans-serif',
                      },
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      sortable: true,
                      render: (row) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: getStatusColor(row.status),
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#525252',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {row.status}
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      key: 'processingTime',
                      label: 'Processing Time',
                      sortable: true,
                      cellSx: {
                        fontSize: '0.8125rem',
                        color: '#525252',
                        fontFamily: 'Inter, sans-serif',
                      },
                    },
                    {
                      key: 'action',
                      label: 'Action',
                      render: () => (
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: '#0284C7',
                            fontFamily: 'Inter, sans-serif',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                              color: '#0369A1',
                            },
                          }}
                        >
                          Inspect
                        </Typography>
                      ),
                    },
                  ]}
                  data={tableData}
                  loading={false}
                  showSerialNumber={false}
                  emptyMessage="No data available."
                />
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default LiveStreamingPage;
